import { useTranslation } from 'react-i18next';

import { APP_STORE_URL, type AppStore, PLAY_STORE_URL } from '@/lib/store-links';
import { cn } from '@/lib/utils';

/**
 * The two app stores as QR tiles a reader scans from their desk, each also a
 * link — the landing top bar's "Get the app" panel and the in-app "Download
 * our companion app" dialog show the SAME tiles (one component, so the codes,
 * their quiet zone and their glyphs cannot drift apart).
 *
 * The codes are generated once (`qrcode -e H`, error correction H so the
 * store glyph can sit on them) into `public/assets/landing/qr/`. The glyph
 * sits in the square the code leaves empty at its centre: the App Store icon
 * as is, and Google Play's triangle without the white disc its app icon
 * carries, so neither theme shows a white patch.
 *
 * // single-platform-only: a QR code is how a DESKTOP visitor gets a phone
 * // app; the app itself has nothing to scan it with.
 */
const STORE_QR_LINKS = [
  {
    id: 'ios',
    href: APP_STORE_URL,
    glyph: '/assets/landing/app-store.png',
    glyphClass: 'h-6 w-6 rounded-[5px]',
    qr: '/assets/landing/qr/app-store.svg',
    labelKey: 'landingV3.nav.appStore',
  },
  {
    id: 'android',
    href: PLAY_STORE_URL,
    glyph: '/assets/landing/play-store-glyph.png',
    glyphClass: 'h-auto w-[22px]',
    qr: '/assets/landing/qr/play-store.svg',
    labelKey: 'landingV3.nav.playStore',
  },
] as const satisfies readonly {
  id: AppStore;
  href: string;
  glyph: string;
  glyphClass: string;
  qr: string;
  labelKey: string;
}[];

export function StoreQrTiles({
  onOpen,
  className,
}: {
  /** A tile was followed (analytics); the link itself opens the store. */
  onOpen?: (store: AppStore) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={cn('grid grid-cols-2 gap-3', className)}>
      {STORE_QR_LINKS.map((store) => (
        <a
          key={store.id}
          href={store.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onOpen?.(store.id)}
          className="group flex flex-col items-center gap-3 rounded-[18px] border border-secondary-200 bg-secondary-100 p-3 transition-colors hover:bg-secondary-200">
          {/* The code has no plate: the SVG is modules only and acts as a
              MASK over a DS colour, so it reads dark on light and light on
              dark and sits flush on the tile. The tile's own padding is the
              quiet zone, and the tile is OPAQUE in every state: at 70% the
              violet of a card behind the panel showed through and the App
              Store code stopped scanning. The store glyph sits in the 13x13
              square the code leaves empty (error correction H carries it). */}
          {/* 147 = 49 modules x 3px: whole pixels per module at 1x and 2x,
              so every module renders the same width (at 132 they came out 2
              or 3px wide and 1x screens stopped reading the App Store code). */}
          <span className="relative block h-[147px] w-[147px]">
            {/* Decorative for assistive tech: the tile is a link that already
                reads the store's name. */}
            <span
              aria-hidden
              className="block h-full w-full bg-secondary-800"
              style={{
                maskImage: `url(${store.qr})`,
                WebkitMaskImage: `url(${store.qr})`,
                maskSize: '100% 100%',
                WebkitMaskSize: '100% 100%',
              }}
            />
            <img
              src={store.glyph}
              alt=""
              className={cn(
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                store.glyphClass,
              )}
            />
          </span>
          <span className="text-[14px] font-semibold leading-5 text-secondary-700">
            {t(store.labelKey)}
          </span>
        </a>
      ))}
    </div>
  );
}
